/**
 * Turns a TimelineLayout into DOM, using the class names ported verbatim from
 * prototypes/mode-a.html. All geometry arrives pre-computed: this file decides
 * nothing, it only places what layout.ts worked out.
 */

import type { ChronoDocument } from '../model/types';
import type { BandLayout, TimelineLayout } from './layout';
import { axisLabelTop, whiskerTop, type Metrics } from './metrics';

const px = (value: number) => `${Math.round(value * 100) / 100}px`;

export function renderTimeline(
  container: HTMLElement,
  doc: ChronoDocument,
  layout: TimelineLayout,
  metrics: Metrics,
  selectedIndex: number | null = null,
): void {
  container.replaceChildren();
  for (const band of layout.bands) {
    container.append(renderBand(band, doc, metrics, selectedIndex));
  }
}

function renderBand(
  layout: BandLayout,
  doc: ChronoDocument,
  metrics: Metrics,
  selectedIndex: number | null,
): HTMLElement {
  const band = document.createElement('div');
  band.className = 'band';
  if (layout.band.pageBreakBefore) band.classList.add('page-break-before');

  // On screen the name sits above the body, because the body scrolls sideways
  // and a name pinned to its left would scroll out of view. On paper there is
  // nothing to scroll, so it moves into the gutter beside the axis and stops
  // costing a line of its own.
  const label = document.createElement('div');
  label.className = 'band-label';
  label.textContent = layout.band.label;
  if (metrics.gutterW > 0) {
    band.classList.add('gutter');
    label.style.top = px(layout.eventsHeight);
    label.style.height = px(layout.axisHeight);
  }

  const body = document.createElement('div');
  body.className = 'band-body';
  body.style.width = px(layout.width);
  // layout.height also counts the title line, which is a sibling of the body.
  body.style.height = px(layout.eventsHeight + layout.axisHeight + layout.periodsHeight);

  // Guides go in first so the bars, in a later positioned sibling, paint over
  // them — and they start inside the axis area so they reach the axis line
  // itself rather than stopping under the tick labels (features.md §6bis).
  for (const bar of layout.bars) {
    for (const guideX of [bar.guideStart, bar.guideEnd]) {
      if (guideX === null) continue;
      const guide = document.createElement('div');
      guide.className = 'date-guide';
      guide.style.left = px(guideX);
      guide.style.top = px(layout.eventsHeight);
      guide.style.height = px(layout.axisHeight + bar.lane * metrics.periodLaneH + metrics.periodLaneH / 2);
      body.append(guide);
    }
  }

  body.append(
    renderEvents(layout, metrics, selectedIndex),
    renderAxis(layout, metrics),
    renderPeriods(layout, doc, metrics, selectedIndex),
  );

  band.append(label, body);
  return band;
}

function renderEvents(layout: BandLayout, metrics: Metrics, selectedIndex: number | null): HTMLElement {
  const row = document.createElement('div');
  row.className = 'events-row';
  row.style.height = px(layout.eventsHeight);

  for (const event of layout.events) {
    // Lane 0 sits closest to the axis; later lanes stack upwards.
    const top = layout.eventsHeight - (event.lane + 1) * metrics.eventLaneH;

    if (event.whisker) {
      const whisker = document.createElement('div');
      whisker.className = 'event-span';
      whisker.style.left = px(event.whisker.x);
      whisker.style.width = px(event.whisker.width);
      whisker.style.top = px(whiskerTop(metrics, top));
      row.append(whisker);
    }

    const marker = document.createElement('div');
    marker.className = 'event';
    marker.classList.toggle('selected', event.index === selectedIndex);
    marker.classList.toggle('flip', event.labelAnchor === 'end');
    marker.dataset['itemIndex'] = String(event.index);
    marker.style.left = px(event.x);
    marker.style.top = px(top);
    marker.textContent = event.text;
    row.append(marker);
  }

  return row;
}

function renderAxis(layout: BandLayout, metrics: Metrics): HTMLElement {
  const row = document.createElement('div');
  row.className = 'axis-row';
  row.style.height = px(layout.axisHeight);

  const line = document.createElement('div');
  line.className = 'axis-line';
  row.append(line);

  for (const tick of layout.ticks) {
    const mark = document.createElement('div');
    mark.className = tick.major ? 'tick major' : 'tick';
    mark.style.left = px(tick.x);
    mark.style.height = px(tick.major ? metrics.majorTickLen : metrics.tickLen);
    row.append(mark);

    if (tick.label === null) continue;

    const text = document.createElement('div');
    text.className = tick.labelBold ? 'tick-label bold' : 'tick-label';
    text.style.left = px(tick.labelX);
    text.style.top = px(axisLabelTop(metrics));
    text.textContent = tick.label;
    row.append(text);
  }

  return row;
}

function renderPeriods(
  layout: BandLayout,
  doc: ChronoDocument,
  metrics: Metrics,
  selectedIndex: number | null,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'periods-row';
  row.style.height = px(layout.periodsHeight);

  for (const bar of layout.bars) {
    const el = document.createElement('div');
    el.className = 'bar';
    el.classList.toggle('cut-start', bar.cutStart);
    el.classList.toggle('cut-end', bar.cutEnd);
    el.classList.toggle('selected', bar.index === selectedIndex);
    el.classList.toggle('pinned', bar.item.lane !== undefined);
    el.classList.toggle('label-end', bar.labelAnchor === 'end');
    el.dataset['itemIndex'] = String(bar.index);

    el.style.left = px(bar.x);
    el.style.width = px(bar.width);
    el.style.top = px(bar.lane * metrics.periodLaneH);
    el.style.background = doc.categories[bar.item.cat]?.color ?? '#cccccc';
    el.textContent = bar.label;

    row.append(el);
  }

  return row;
}
